import styled from '@emotion/styled';
import { theme as systemDefaultTheme } from '../../theme';
import { isPlainObject } from '../../utils';
import {
  defaultOverridesResolver,
  lowercaseFirstLetter,
  processStyleArg,
  internal_processStyles as processStyles,
  resolveTheme,
} from './helper';

/**
 * html태그와 emotion styled 옵션 및 커스텀 옵션을 받아
 * 스타일이 적용된 컴포넌트를 만드는 styled 함수를 반환
 * @returns {(tag: string, inputOptions?: object) => Function}
 */
export default function createStyled() {
  return (tag, inputOptions = {}) => {
    processStyles(tag, (styles) => styles.filter((style) => !style?.__mui_systemSx));

    const { componentName, slot, shouldForwardProp, ...options } = inputOptions;

    const overridesResolver = defaultOverridesResolver(lowercaseFirstLetter(slot));

    const defaultStyledResolver = styled(tag, {
      shouldForwardProp,
      ...options,
    });

    // 스타일 인자를 변환하는 함수. 이 함수는 스타일 인자가 함수 혹은 객체일 경우에 대한 처리를 담당
    const transformStyleArg = (stylesArg) => {
      if (
        (typeof stylesArg === 'function' && stylesArg.__emotion_real !== stylesArg) ||
        isPlainObject(stylesArg) ||
        Array.isArray(stylesArg)
      ) {
        return (props) => {
          return processStyleArg(stylesArg, {
            ...props,
            theme: resolveTheme({ theme: props.theme, defaultTheme: systemDefaultTheme }),
          });
        };
      }

      return stylesArg;
    };

    const sdStyledResolver = (styleArg, ...expressions) => {
      if (Array.isArray(styleArg)) {
        return defaultStyledResolver(styleArg, ...expressions.map(transformStyleArg));
      }

      const transformedStyleArg = transformStyleArg(styleArg);
      const expressionsWithDefaultTheme = expressions ? expressions.map(transformStyleArg) : [];

      if (componentName) {
        // 테마에서 설정한 styleOverrides에 대한 처리
        expressionsWithDefaultTheme.push((props) => {
          const theme = resolveTheme({ ...props });
          if (
            !theme.components ||
            !theme.components[componentName] ||
            !theme.components[componentName].styleOverrides
          ) {
            return null;
          }
          const styleOverrides = theme.components[componentName].styleOverrides;
          const resolvedStyleOverrides = {};
          Object.entries(styleOverrides).forEach(([slotKey, slotStyle]) => {
            resolvedStyleOverrides[slotKey] = processStyleArg(slotStyle, { ...props, theme });
          });
          return overridesResolver(props, resolvedStyleOverrides);
        });

        // 테마에서 설정한 variants에 대한 처리
        expressionsWithDefaultTheme.push((componentProps) => {
          const theme = resolveTheme({
            ...componentProps,
            defaultTheme: systemDefaultTheme,
            theme: componentProps.theme,
          });

          if (
            !theme.components ||
            !theme.components[componentName] ||
            !theme.components[componentName].variants
          ) {
            return null;
          }

          const themeVariants = theme?.components?.[componentName]?.variants;

          const variantStyleOfSlots = {};
          themeVariants.forEach((variant) => {
            Object.entries(variant.styles).forEach(([styleKey, styleValue]) => {
              if (!variantStyleOfSlots[styleKey]) {
                variantStyleOfSlots[styleKey] = { variants: [] };
              }

              variantStyleOfSlots[styleKey].variants.push({
                props: variant.props,
                style: styleValue,
              });
            });
          });

          const resolvedVariantStyles = overridesResolver(componentProps, variantStyleOfSlots);
          return processStyleArg(resolvedVariantStyles, { ...componentProps, theme });
        });
      }

      const Component = defaultStyledResolver(transformedStyleArg, ...expressionsWithDefaultTheme);
      return Component;
    };

    if (defaultStyledResolver.withConfig) {
      sdStyledResolver.withConfig = defaultStyledResolver.withConfig;
    }

    return sdStyledResolver;
  };
}
